import type { CSSInterpolation } from '@emotion/serialize';
import _ from 'lodash';
import type { ComponentPropsWithoutRef, ReactNode, RefObject } from 'react';
import { Fragment, useEffect, useRef } from 'react';

import { Tag } from '@adflow/frontend/ui-common';
import { su } from '@adflow/frontend/ui-theme';
import { useHighlightMeta } from '@adflow/frontend/util-common';

/**
 * The properties of a tag that TagsCell uses.
 *
 * We use this type instead of ViewTag because TagsCell
 * might be re-used for things that should be displayed
 * exactly the same as tags but are not actually tags.
 */
export interface TagsCellTag {
	id: number;
	name: string;
	color: string;
	type?: string;
}

export interface TagsCellProps {
	tags: readonly Readonly<TagsCellTag>[];
	columnWidth?: number;
	textStyles?: CSSInterpolation;
}

export function TagsCell({ tags, textStyles }: TagsCellProps) {
	const { highlightedText } = useHighlightMeta();

	return (
		<TruncatedList
			css={{
				display: 'flex',
				flexWrap: 'wrap',
				alignItems: 'center',
				width: '100%',
				height: '100%',
				gap: '0.25rem',

				// `line-height: 0` is needed so that the li elements will not be larger than the Tag elements.
				// The TruncatedList does not work when the li elements are not `display: list-item`.
				lineHeight: 0,
			}}
			renderTruncator={({ hiddenItemsCount }) => (
				<div css={[su.textXs, { paddingLeft: '0.25rem', maxWidth: 22 }]}>
					+{hiddenItemsCount}
				</div>
			)}
			truncatorWidth={22}
			items={[...tags]
				// sort mutates the array so we must make a copy of the array before sorting.
				// If we mutate a prop it will cause a lot of re-renders and strange behavior.
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((tag) => (
					<Tag
						textStyles={textStyles}
						key={tag.id}
						text={tag.name}
						color={tag.color}
						global={tag.type === 'global'}
						searchTerm={highlightedText}
					/>
				))}
		/>
	);
}

/**
 * Below is a modified version of 'react-truncate-list'
 * optimized for the tags cell to be used in data tables.
 * DO NOT USE IN OTHER LOCATIONS.
 */

type RectMeasurements = Pick<DOMRect, 'bottom' | 'top' | 'left' | 'right'>;
const rectContainsRect = (
	parent: RectMeasurements,
	child: RectMeasurements
) => {
	return (
		child.top >= parent.top &&
		child.bottom <= parent.bottom &&
		child.left >= parent.left &&
		child.right <= parent.right
	);
};

type RenderTruncator = ({
	hiddenItemsCount,
}: {
	hiddenItemsCount: number;
}) => ReactNode;

export interface TruncatedListProps extends Omit<
	ComponentPropsWithoutRef<'ul'>,
	'children'
> {
	renderTruncator: RenderTruncator;
	items: ReactNode[];
	truncatorWidth: number;
	alwaysShowTruncator?: boolean;
}

function TruncateEffect({
	containerRef,
	alwaysShowTruncator,
	truncatorWidth,
}: {
	containerRef: RefObject<HTMLUListElement>;
	truncatorWidth: number;
	alwaysShowTruncator?: boolean;
}) {
	const initialized = useRef(false);
	useEffect(() => {
		const truncate = () => {
			if (!containerRef.current) {
				return;
			}
			// eslint-disable-next-line unicorn/prefer-spread
			const childNodes = Array.from(
				containerRef.current.children
			) as HTMLElement[];

			// If there are no items (the last truncator is always included).
			if (childNodes.length === 1) {
				return;
			}

			if (!containerRef.current) {
				return;
			}
			// If being called a second time, then we need
			// to mark all items as shown to recalculate
			if (initialized.current) {
				for (let i = childNodes.length - 2; i >= 1; i -= 2) {
					const itemEl = childNodes[i]!;
					const truncatorEl = childNodes[i - 1]!;
					truncatorEl.hidden = true;
					itemEl.hidden = false;
				}
			} else {
				initialized.current = true;
			}

			//
			// Test if truncation is necessary.
			//
			if (alwaysShowTruncator) {
				// if the last truncator fits, exit
				const truncatorEl = childNodes.at(-1)!;
				truncatorEl.hidden = false;

				if (
					rectContainsRect(
						containerRef.current.getBoundingClientRect(),
						truncatorEl.getBoundingClientRect()
					)
				) {
					return;
				}
				truncatorEl.hidden = true;
			} else {
				// if the last item fits, exit
				const itemEl = childNodes.at(-2)!;

				if (
					rectContainsRect(
						containerRef.current.getBoundingClientRect(),
						itemEl.getBoundingClientRect()
					)
				) {
					return;
				}
			}

			const containerRect = containerRef.current.getBoundingClientRect();
			function markItemsAsHidden(i: number) {
				const truncatorEl = childNodes[i - 1]!;
				truncatorEl.hidden = false;
				for (const j of _.range(i, childNodes.length, 2)) {
					childNodes[j]!.hidden = true;
				}
			}

			//
			// Go backward and find the last item that can fit.
			//
			for (let i = childNodes.length - 2; i >= 1; i -= 2) {
				const itemEl = childNodes[i]!;

				const itemRect = itemEl.getBoundingClientRect();
				const containsWithRoomRightForTruncator = rectContainsRect(
					{
						top: containerRect.top,
						bottom: containerRect.bottom,
						left: containerRect.left,
						right: containerRect.right - truncatorWidth,
					},
					itemRect
				);

				const containsWithRoomBelowForTruncator = rectContainsRect(
					containerRect,
					{
						top: itemRect.top,
						// assume truncator is same height as item
						bottom: itemRect.bottom + itemRect.height,
						left: itemRect.left,
						right: itemRect.right,
					}
				);

				if (
					containsWithRoomRightForTruncator ||
					containsWithRoomBelowForTruncator
				) {
					markItemsAsHidden(i + 2);
					return;
				}
			}

			// none fit, so mark all items hidden
			markItemsAsHidden(1);
		};
		// truncate();
		const truncateDebounce = _.debounce(truncate, 50);

		const resizeObserver = new ResizeObserver((entries) => {
			for (const __ of entries) {
				truncateDebounce();
			}
		});

		if (containerRef.current) {
			resizeObserver.observe(containerRef.current);
		}

		return () => {
			resizeObserver.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [alwaysShowTruncator, truncatorWidth]);

	return null;
}

export function TruncatedList({
	renderTruncator,
	alwaysShowTruncator,
	items,
	truncatorWidth,
	...ulProps
}: TruncatedListProps) {
	const containerRef = useRef<HTMLUListElement>(null);

	const itemsWithTruncator = items.map((item, i) => (
		<Fragment key={i}>
			<li hidden>{renderTruncator({ hiddenItemsCount: items.length - i })}</li>
			<li css={{ maxWidth: `calc(100% - ${truncatorWidth}px)` }}>{item}</li>
		</Fragment>
	));

	return (
		<>
			<TruncateEffect
				containerRef={containerRef}
				alwaysShowTruncator={alwaysShowTruncator}
				truncatorWidth={truncatorWidth}
			/>

			<ul ref={containerRef} {...ulProps}>
				{itemsWithTruncator}

				<li hidden>{renderTruncator({ hiddenItemsCount: 0 })}</li>
			</ul>
		</>
	);
}
